// Calendar Page - Display events and task deadlines
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchCalendarEvents, fetchCalendarEventsAll, fetchTenantUsers, fetchCrmEntities } from '../../lib/queryFunctions';
import { useAuth } from '../../context/AuthContext';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Calendar } from '../../components/ui/calendar';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus, Search, Users } from 'lucide-react';
import { EventDetailDialog, EventDialog } from '../../components/crm/ActivityDialogs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { TableToolbar } from '../../components/common/TableToolbar';

const CalendarPage = () => {
  const { isViewOnly, user } = useAuth();
  const viewOnly = isViewOnly();
  const isTeamLeader = user?.roles?.includes('tenant_team_leader');
  const navigate = useNavigate();
  const qc = useQueryClient();

  // ── UI state ──────────────────────────────────────────────────────────────
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [eventSearch, setEventSearch] = useState('');
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [showDeptView, setShowDeptView] = useState(false);

  // ── Server state ──────────────────────────────────────────────────────────
  const startDate = useMemo(() =>
    new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString().split('T')[0],
  [currentMonth]);
  const endDate = useMemo(() =>
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).toISOString().split('T')[0],
  [currentMonth]);

  const { data: allEvents = [], isLoading: loading } = useQuery({
    queryKey: QK.calendarEvents(startDate, endDate),
    queryFn: fetchCalendarEvents(startDate, endDate),
    onError: () => toast.error('Failed to load calendar events'),
  });
  const events = allEvents;

  const { data: listEvents = [] } = useQuery({
    queryKey: QK.calendarEventsAll(),
    queryFn: fetchCalendarEventsAll(),
  });

  const { data: usersData = [] } = useQuery({
    queryKey: QK.tenantUsers(),
    queryFn: fetchTenantUsers(),
  });
  const fullUsers = usersData;
  const usersMap = useMemo(() => Object.fromEntries(
    usersData.map(u => [u.id, u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email])
  ), [usersData]);

  const { data: entitiesData = [] } = useQuery({
    queryKey: QK.crmEntities(),
    queryFn: fetchCrmEntities(),
  });
  const entities = useMemo(() => Object.fromEntries(entitiesData.map(e => [e.id, e])), [entitiesData]);

  const invalidateEvents = () => {
    qc.invalidateQueries({ queryKey: QK.calendarEvents(startDate, endDate) });
    qc.invalidateQueries({ queryKey: QK.calendarEventsAll() });
  };

  // Compute dept user IDs for team leader filtering
  const getDeptUserIds = () => {
    const dept = user?.team_leader_department || user?.department;
    if (!dept) return new Set([user?.id]);
    const ids = fullUsers
      .filter(u => u.department === dept || u.id === user?.id)
      .map(u => u.id);
    return new Set(ids.length ? ids : [user?.id]);
  };

  // Apply dept filter when toggled — system-wide events (no created_by_id) always pass through
  const applyDeptFilter = (eventsArr) => {
    if (showDeptView) {
      const deptIds = getDeptUserIds();
      return eventsArr.filter(e => !e.created_by_id || e.system_wide || e.event_type === 'deadline' || deptIds.has(e.created_by_id));
    }
    // Default: show own events + system/tenant-wide events (applies to all users including team leaders)
    return eventsArr.filter(e => !e.created_by_id || e.system_wide || e.event_type === 'deadline' || e.created_by_id === user?.id);
  };

  // --- Dept View Avatar Helpers ---
  const AVATAR_COLORS = [
    '#3b82f6', '#8b5cf6', '#f97316', '#ef4444',
    '#06b6d4', '#eab308', '#6366f1', '#14b8a6', '#ec4899', '#84cc16'
  ];
  const getUserColor = (userId) => {
    if (!userId) return '#9ca3af';
    let hash = 0;
    for (let i = 0; i < userId.length; i++) hash = (hash + userId.charCodeAt(i)) % AVATAR_COLORS.length;
    return AVATAR_COLORS[hash];
  };
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  // --- End Avatar Helpers ---

  const eventsForSelectedDate = selectedDate ? applyDeptFilter(events).filter(event => {
    const eventStart = event.start_date_time || event.event_date;
    const eventEnd = event.end_date_time || eventStart;
    
    if (!eventStart) return false;
    
    const eventStartDate = new Date(eventStart);
    const eventEndDate = new Date(eventEnd);
    const selected = new Date(selectedDate);
    
    // Remove time for comparison
    selected.setHours(0, 0, 0, 0);
    eventStartDate.setHours(0, 0, 0, 0);
    eventEndDate.setHours(0, 0, 0, 0);
    
    return selected >= eventStartDate && selected <= eventEndDate;
  }) : [];

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return applyDeptFilter(events).filter(event => {
      if (!event.start_date_time) return false;
      const startDate = new Date(event.start_date_time);
      const endDate = event.end_date_time ? new Date(event.end_date_time) : startDate;
      
      // Check if date falls within event range
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  const getEventTypeBadgeColor = (eventType) => {
    const colors = {
      birthday: 'bg-blue-100 text-blue-800',
      race: 'bg-purple-100 text-purple-800',
      sale: 'bg-green-100 text-green-800',
      deadline: 'bg-red-100 text-red-800',
      reminder: 'bg-orange-100 text-orange-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colors[eventType] || colors.other;
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      birthday: 'bg-blue-100 text-blue-800 border-blue-300',
      race: 'bg-purple-100 text-purple-800 border-purple-300',
      sale: 'bg-green-100 text-green-800 border-green-300',
      deadline: 'bg-red-100 text-red-800 border-red-300',
      reminder: 'bg-orange-100 text-orange-800 border-orange-300',
      other: 'bg-white text-gray-800 border-gray-300',
    };
    return colors[eventType] || colors.other;
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setSelectedDate(today);
  };

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-foreground">Calendar</h1>
            <p className="text-muted-foreground mt-1">View your events and deadlines</p>
          </div>
          <div className="flex gap-2">
            {isTeamLeader && (
              <div className="flex items-center gap-2" data-testid="calendar-dept-toggle">
                <Switch
                  id="calendar-dept-switch"
                  checked={showDeptView}
                  onCheckedChange={setShowDeptView}
                />
                <Label htmlFor="calendar-dept-switch" className="text-sm cursor-pointer flex items-center gap-1.5 select-none">
                  <Users className="w-3.5 h-3.5" />
                  Department View
                </Label>
              </div>
            )}
            {!viewOnly && (
            <Button onClick={() => setEventDialogOpen(true)} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              New Event
            </Button>
            )}
            <Button onClick={goToToday} variant="outline">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Today
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 [@media(min-width:1400px)]:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-serif text-xl">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={previousMonth}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={nextMonth}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="relative">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      setSelectedDate(date);
                    }
                  }}
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  className="rounded-md w-full"
                  classNames={{
                    months: "flex flex-col w-full",
                    month: "space-y-2 w-full",
                    caption: "hidden",
                    caption_label: "hidden",
                    nav: "hidden",
                    nav_button: "hidden",
                    nav_button_previous: "hidden",
                    nav_button_next: "hidden",
                    table: "w-full border-collapse [table-layout:fixed]",
                    head_row: "",
                    head_cell: "text-muted-foreground font-semibold text-sm text-center pb-2 w-[14.28%] overflow-hidden",
                    row: "",
                    cell: "relative p-0.5 text-center text-sm w-[14.28%] min-h-[100px] overflow-hidden align-top",
                    day: "h-full w-full p-1 font-normal hover:bg-accent rounded-md flex flex-col items-start justify-start cursor-pointer overflow-hidden",
                    day_selected: "bg-primary/20 text-primary font-bold",
                    day_today: "",
                    day_outside: "text-muted-foreground opacity-20",
                    day_disabled: "text-muted-foreground opacity-30",
                  }}
                  components={{
                    DayContent: ({ date }) => {
                      const dayEvents = getEventsForDate(date);
                      const uniqueEventTypes = [...new Set(dayEvents.map(e => e.event_type))];

                      // Department View: keep shaded event type boxes, add user avatar on right
                      if (showDeptView && dayEvents.length > 0) {
                        return (
                          <div className="h-full w-full flex flex-col items-start justify-start">
                            <span className="text-sm mb-1">{date.getDate()}</span>
                            <div className="flex flex-col gap-0.5 w-full min-w-0">
                              {dayEvents.slice(0, 3).map((event, idx) => (
                                <div
                                  key={idx}
                                  title={usersMap[event.created_by_id] ? `${event.event_type?.replace('_', ' ')} · ${usersMap[event.created_by_id]}` : event.event_type?.replace('_', ' ')}
                                  className={`flex items-center justify-between gap-0.5 px-1.5 py-0.5 rounded min-w-0 ${getEventTypeBadgeColor(event.event_type)}`}
                                >
                                  <span className="text-[9px] capitalize font-medium truncate flex-1 min-w-0">
                                    {event.event_type?.replace('_', ' ')}
                                  </span>
                                  {event.created_by_id && (
                                    <div
                                      style={{ backgroundColor: getUserColor(event.created_by_id) }}
                                      className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[7px] font-bold text-white shrink-0 shadow-sm"
                                    >
                                      {getInitials(usersMap[event.created_by_id])}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {dayEvents.length > 3 && (
                                <span className="text-[8px] text-muted-foreground">
                                  +{dayEvents.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="h-full w-full flex flex-col items-start justify-start min-w-0 overflow-hidden">
                          <span className="text-sm mb-1 shrink-0">{date.getDate()}</span>
                          {uniqueEventTypes.length > 0 && (
                            <div className="flex flex-col gap-0.5 w-full min-w-0">
                              {uniqueEventTypes.slice(0, 3).map((type, idx) => (
                                <span 
                                  key={idx}
                                  className={`text-[9px] px-1.5 py-0.5 rounded truncate capitalize font-medium block min-w-0 ${getEventTypeBadgeColor(type)}`}
                                  title={type?.replace('_', ' ')}
                                >
                                  {type?.replace('_', ' ')}
                                </span>
                              ))}
                              {dayEvents.length > 3 && (
                                <span className="text-[8px] text-muted-foreground">
                                  +{dayEvents.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                  }}
                  modifiers={{
                    today: (date) => {
                      return date.toDateString() === new Date().toDateString();
                    }
                  }}
                  modifiersStyles={{
                    today: { 
                      backgroundColor: 'rgba(52, 107, 78, 0.1)',
                      border: '3px solid #346B4E',
                      borderRadius: '0.375rem',
                      fontWeight: 'bold'
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Events for Selected Date */}
          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-xl">
                {selectedDate ? selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Select a date'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {eventsForSelectedDate.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">No events on this date</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {eventsForSelectedDate.map(event => {
                    const eventDate = event.start_date_time ? new Date(event.start_date_time) : null;
                    const creatorName = usersMap[event.created_by_id];
                    return (
                      <div 
                        key={event.id} 
                        className="p-3 border rounded cursor-pointer hover:shadow-sm transition-shadow hover:bg-muted/50"
                        onClick={() => {
                          setSelectedEvent(event);
                          setEventDetailOpen(true);
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={`text-xs capitalize ${getEventTypeColor(event.event_type)}`}>
                            {event.event_type?.replace('_', ' ') || 'Event'}
                          </Badge>
                          {showDeptView && event.created_by_id && creatorName && (
                            <div className="flex items-center gap-1.5">
                              <div
                                style={{ backgroundColor: getUserColor(event.created_by_id) }}
                                className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shadow-sm shrink-0"
                                title={creatorName}
                              >
                                {getInitials(creatorName)}
                              </div>
                              <span className="text-xs text-muted-foreground truncate max-w-[80px]">{creatorName}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm">{event.activity_name}</p>
                          <span className="text-xs font-medium text-muted-foreground ml-3">
                            {event.all_day ? 'All Day' : eventDate?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-xs mt-1 text-muted-foreground line-clamp-2">{event.description}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* All Events Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="font-serif text-xl">
              {showDeptView ? 'Department Events' : 'All Events'}
            </CardTitle>
            <div className="flex flex-col gap-2 mt-2">
              {/* Search — full width */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search events..." value={eventSearch} onChange={(e) => setEventSearch(e.target.value)} className="pl-9 w-full" />
              </div>
              {/* Buttons row */}
              <div className="flex items-center gap-2">
                <Button
                  variant={showPastEvents ? 'default' : 'outline'}
                  size="sm"
                  className={`h-8 text-xs ${showPastEvents ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                  onClick={() => setShowPastEvents(!showPastEvents)}
                >
                  {showPastEvents ? 'Showing All' : 'Include Past'}
                </Button>
                <TableToolbar
                  inline
                  tableId="calendar-events"
                  data={(() => {
                    const q = eventSearch.toLowerCase();
                    const now = new Date();
                    return applyDeptFilter(listEvents).filter(e => {
                      if (!showPastEvents && e.start_date_time && new Date(e.start_date_time) < now) return false;
                      return !q || [e.activity_name, e.description, e.event_type, e.start_date_time].some(v => (v || '').toLowerCase().includes(q));
                    });
                  })()}
                  filename="events"
                  columns={[
                    { key: 'activity_name', label: 'Event' },
                    { key: 'event_type', label: 'Type' },
                    { key: 'start_date_time', label: 'Start Date', accessor: r => r.start_date_time ? new Date(r.start_date_time).toLocaleDateString() : '-' },
                    { key: 'start_time', label: 'Time', accessor: r => r.start_date_time ? new Date(r.start_date_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '-' },
                    { key: 'description', label: 'Description' },
                  ]}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 py-0 pb-4">
            <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <Table data-table-id="calendar-events">
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const q = eventSearch.toLowerCase();
                  const now = new Date();
                  const filtered = applyDeptFilter(listEvents).filter(e => {
                    if (!showPastEvents && e.start_date_time && new Date(e.start_date_time) < now) return false;
                    return !q || [e.activity_name, e.description, e.event_type, e.start_date_time].some(v => (v || '').toLowerCase().includes(q));
                  }).sort((a, b) => {
                    const da = a.start_date_time ? new Date(a.start_date_time) : new Date(0);
                    const db = b.start_date_time ? new Date(b.start_date_time) : new Date(0);
                    return da - db;
                  });
                  return filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No events found</TableCell>
                    </TableRow>
                  ) : filtered.map(event => (
                    <TableRow key={event.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedEvent(event); setEventDetailOpen(true); }}>
                      <TableCell className="text-sm font-medium">{event.activity_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize pointer-events-none">{event.event_type?.replace(/_/g, ' ') || '-'}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{event.start_date_time ? (event.start_date_time.split('T')[0].replace(/(\d{4})-(\d{2})-(\d{2})/, (_, y, m, d) => `${parseInt(m)}/${parseInt(d)}/${y}`)) : '-'}</TableCell>
                      <TableCell className="text-sm">{event.all_day ? 'All Day' : event.start_date_time ? new Date(event.start_date_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground truncate max-w-[300px]">{event.description || '-'}</TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
        
        {/* Event Detail Dialog */}
        <EventDetailDialog
          open={eventDetailOpen}
          onOpenChange={setEventDetailOpen}
          event={selectedEvent}
          usersMap={usersMap}
          entities={entities}
          onNavigate={(path) => navigate(path)}
          onSuccess={invalidateEvents}
        />
        
        {/* Event Creation Dialog */}
        <EventDialog
          open={eventDialogOpen}
          onOpenChange={setEventDialogOpen}
          onSuccess={invalidateEvents}
        />
      </div>
    </SidebarLayout>
  );
};

export default CalendarPage;
